export function createExclusiveRuntimeOwner() {
  /** @type {{ token: symbol; label: string } | null} */
  let active = null;
  return Object.freeze({
    /** @param {string} label */
    claim(label) {
      if (active) {
        throw new Error(`Cannot start ${label}; ${active.label} already owns the exact model runtime`);
      }
      const token = Symbol(label);
      active = { token, label };
      return () => {
        if (active?.token === token) active = null;
      };
    },
    /** @param {string} label */
    claimLifecycle(label) {
      const release = this.claim(label);
      let runtimeMayExist = false;
      let runtimeTerminal = false;
      let released = false;
      return Object.freeze({
        runtimeMayExist() {
          if (!released) runtimeMayExist = true;
        },
        runtimeTerminal() {
          if (!runtimeMayExist) throw new Error("Cannot mark a runtime terminal before it may exist");
          runtimeTerminal = true;
        },
        releaseIfSafe() {
          if (!released && (!runtimeMayExist || runtimeTerminal)) {
            released = true;
            release();
          }
        },
        get mayExist() {
          return runtimeMayExist;
        },
        get terminal() {
          return runtimeTerminal;
        },
      });
    },
    /** @template T @param {string} label @param {() => Promise<T>} operation @returns {Promise<T>} */
    async run(label, operation) {
      const release = this.claim(label);
      try {
        return await operation();
      } finally {
        release();
      }
    },
    /**
     * @template T
     * @param {string} label
     * @param {(lifecycle: { runtimeMayExist(): void; runtimeTerminal(): void }) => Promise<T>} operation
     * @returns {Promise<T>}
     */
    async runFailClosed(label, operation) {
      const lifecycle = this.claimLifecycle(label);
      try {
        return await operation(lifecycle);
      } finally {
        // If teardown failed after a Worker/runtime might have been created,
        // retain the global lease permanently in this document. Reload is the
        // only safe recovery; a second 7.33-GiB owner must not be admitted.
        lifecycle.releaseIfSafe();
      }
    },
    get activeLabel() {
      return active?.label ?? null;
    },
  });
}

const SHARED_OWNER_KEY = Symbol.for("prime-mach1.exact-runtime-owner.v1");

/** @typedef {ReturnType<typeof createExclusiveRuntimeOwner> & { kind: "prime-mach1-shared-runtime-owner"; schemaVersion: 1 }} SharedRuntimeOwner */
/** @returns {SharedRuntimeOwner} */
function sharedOwner() {
  const scope = /** @type {typeof globalThis & Record<symbol, unknown>} */ (globalThis);
  const existing = scope[SHARED_OWNER_KEY];
  if (existing !== undefined) {
    const candidate = /** @type {Record<string, unknown>} */ (existing);
    if (
      !existing || typeof existing !== "object" ||
      candidate.kind !== "prime-mach1-shared-runtime-owner" ||
      candidate.schemaVersion !== 1 ||
      typeof candidate.claim !== "function" ||
      typeof candidate.claimLifecycle !== "function" ||
      typeof candidate.run !== "function" ||
      typeof candidate.runFailClosed !== "function"
    ) throw new Error("Global exact runtime owner has an invalid identity");
    return /** @type {SharedRuntimeOwner} */ (existing);
  }
  const delegate = createExclusiveRuntimeOwner();
  const owner = Object.freeze({
    kind: "prime-mach1-shared-runtime-owner",
    schemaVersion: 1,
    claim: delegate.claim,
    claimLifecycle: delegate.claimLifecycle,
    run: delegate.run,
    runFailClosed: delegate.runFailClosed,
    get activeLabel() {
      return delegate.activeLabel;
    },
  });
  Object.defineProperty(scope, SHARED_OWNER_KEY, {
    configurable: false,
    enumerable: false,
    writable: false,
    value: owner,
  });
  return /** @type {SharedRuntimeOwner} */ (owner);
}

export const sharedExactRuntimeOwner = sharedOwner();
